// Desktop navy sidebar. A presentation-only category navigator (Home, Dashboards,
// Campus Portals, Pages, Admin Tools, Favourites) — it lists no launch targets of
// its own. Collapse / hover-expand / pin behaviour mirrors the original rail and
// reuses its persisted "pinned" preference.
import React, { useState } from "react";
import { Pin, PinOff } from "lucide-react";
import { THAPAR_LOGO_URL } from "./WorkspaceHeader";

export const SIDEBAR_COLLAPSED_WIDTH = 68;
export const SIDEBAR_EXPANDED_WIDTH = 248;

const SidebarNav = ({ entries, active, onSelect, pinned, onTogglePin }) => {
  const [hovered, setHovered] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const expanded = pinned || hovered || keyboardFocus;

  const handleFocus = (event) => {
    // Expand for keyboard users only; a mouse click must not keep it open.
    try {
      if (event.target.matches(":focus-visible")) setKeyboardFocus(true);
    } catch {
      /* :focus-visible unsupported — hover / pin still work */
    }
  };
  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setKeyboardFocus(false);
  };

  return (
    <div className="cc-side-wrap" style={{ width: pinned ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH }}>
      <aside
        className="cc-side"
        aria-label="Workspace sidebar"
        data-expanded={expanded ? "true" : "false"}
        data-pinned={pinned ? "true" : "false"}
        style={{ width: expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <div className="cc-side__brand">
          <span className="cc-logo">
            <img src={THAPAR_LOGO_URL} alt="Thapar Institute logo" />
          </span>
          <span className="cc-side__brand-text">
            Campus Connect
            <small>TIET workspace</small>
          </span>
        </div>

        <nav className="cc-side__nav" aria-label="Workspace navigation">
          <ul>
            {entries.map(({ id, label, icon: Icon, count }) => (
              <li key={id}>
                <button
                  type="button"
                  className="cc-side__item"
                  aria-current={active === id ? "page" : undefined}
                  title={expanded ? undefined : label}
                  onClick={() => onSelect(id)}
                >
                  <Icon aria-hidden="true" />
                  <span className="cc-side__label">{label}</span>
                  {typeof count === "number" ? <span className="cc-side__count">{count}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="cc-side__foot">
          <button
            type="button"
            className="cc-side__item"
            aria-pressed={pinned}
            aria-label={pinned ? "Unpin sidebar" : "Pin sidebar open"}
            title={expanded ? undefined : "Pin sidebar open"}
            onClick={onTogglePin}
          >
            {pinned ? <PinOff aria-hidden="true" /> : <Pin aria-hidden="true" />}
            <span className="cc-side__label">{pinned ? "Sidebar pinned" : "Pin sidebar"}</span>
          </button>
        </div>
      </aside>
    </div>
  );
};

export default SidebarNav;
